<template>
    <div class="tag-input-field" :class="{'is-focused': isFocused}" v-click-outside="hideSearchResults">
        <div :class="computedClassName">
            <r-badge
                type="tag"
                :close="!computedDisabled"
                @close="removeTag(index)"
                v-for="(badge, index) in computedTagBadges"
                :key="index">
                    <span :class="{'text-muted': computedDisabled}">{{badge}}</span>
            </r-badge>
            <input type="text"
                   ref="taginput"
                   v-show="input || placeholder"
                   :placeholder="placeholder"
                   :disabled="computedDisabled || (!isFocused && loading)"
                   v-model="input"
                   @keydown.enter.prevent="tagFromInput"
                   @keydown.8="removeLastTag"
                   @keydown.down="nextSearchResult"
                   @keydown.up="prevSearchResult"
                   @keyup.esc.prevent.stop="hideSearchResults"
                   @keyup="searchTag"
                   @focus="inputFocus"
                   @blur="inputBlur"
                   @input="searchChange"
                   @value="tags">
            <input type="hidden" v-if="elementId"
                   :name="elementId"
                   :id="elementId"
                   v-model="hiddenInput">
            <v-icon v-if="loading" icon="loading" class="is-spinning icon-light-gray inline-s " style="top:2px;"/>
        </div>
        <p v-show="input && loading" class="typeahead">
            Looking for matching results...
        </p>
        <p v-show="input && !searchResults.length && onlyExistingTags && !loading" class="typeahead">
            No elements found. Consider changing the search query.
        </p>
        <p v-show="input && !searchResults.length && !onlyExistingTags && !loading" class="typeahead">
            <template v-if="tags && tags.includes(input)">
                Tag already selected
            </template>
            <template v-else-if="canAdd">
                Press Enter to Add new Tag.
            </template>
            <template v-else>
                No elements found. Consider changing the search query.
            </template>
        </p>
        <p v-show="hasFirstFocus && searchResults.length" class="typeahead">
            <span class="stack-xs inline-xs display-inline-block" v-if="showPrevButton">
                <v-icon-button
                    ref="buttonPrevItemsPage"
                    type="basic"
                    size="small"
                    @click="handleEmitPrevItemsPageClick"
                    fluid>
                    <v-icon icon="caret-left"/>
                    Get prev
                </v-icon-button>
            </span>
            <span
                v-for="(tag, index) in searchResults"
                :key="index"
                class="display-inline-block">
                <span @mousedown.prevent="tagFromSearchOnClick(tag)">
                    <r-badge
                        type="tag"
                        class="inline-xs stack-xs cursor-pointer"
                        :class="{
                    'background-light-blue': index === searchSelection
                  }">{{tag.text}}</r-badge>
                </span>
            </span>
            <span class="stack-xs display-inline-block" v-if="showNextButton">
                <v-icon-button
                    ref="buttonNextItemsPage"
                    type="basic"
                    size="small"
                    @click="handleEmitNextItemsPageClick"
                    fluid>
                    Load more...
                <v-icon icon="caret-right"/>
            </v-icon-button>
            </span>

        </p>
    </div>
</template>
<script>
    import VoerroTagsInput from '@voerro/vue-tagsinput';
    import vIcon from '@/components/form/v-icon';
    import vIconButton from '@/components/form/v-icon-button';

    export default {
        name: 'extend-tag',
        extends: VoerroTagsInput,
        components: {vIcon, vIconButton},
        props: {
            canAdd: {
                type: Boolean,
                default: false,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            /**
             * Delete field borders and padding
             * only for preview badges list
             */
            preview: {
                type: Boolean,
                default: false,
            },
            showPrevButton: {
                type: Boolean,
                default: false,
            },
            showNextButton: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                isFocused: false,
                hasFirstFocus: false,
            };
        },
        computed: {
            hasTagBadges() {
                return !!this.tagBadges.length;
            },
            computedDisabled() {
                return this.preview || this.disabled;
            },
            computedClassName() {
                return {
                    'field-input': !this.preview,
                    'tags-input': true,
                    'display-flex': true,
                    [this.wrapperClass]: !!this.wrapperClass && !this.preview,
                    'tags-input-has-value': !!this.tagBadges.length,
                    'padding-top-s': !!this.disabled && !this.preview,
                    'padding-bottom-xs': !!this.disabled && !this.preview,
                    'padding-left-s': !!this.hasTagBadges && !this.preview,
                    'no-padding-right': !!this.loading && !this.preview,
                };
            },
            computedTagBadges() {
                // https://github.com/voerro/vue-tagsinput/blob/master/src/VoerroTagsInput.vue#L238
                return this.tagBadges.map(text => text.replace(/&nbsp;/g, ' '));
            },
        },
        methods: {
            inputFocus() {
                this.isFocused = true;
                this.hasFirstFocus = true;
                // npm package esc bug.
                // write label, click esc, focus input
                if (this.input !== '' && this.input === this.oldInput) {
                    this.oldInput = '';
                }
                this.onFocus();
            },
            inputBlur(event) {
                this.isFocused = false;
                this.$nextTick(() => {
                    if (!event) {
                        return;
                    }
                    if (!event.relatedTarget) {
                        // TODO check crossbrowser
                        this.hideTypeahead();
                    } else {
                        const nextButton = this.$refs.buttonNextItemsPage;
                        const prevButton = this.$refs.buttonPrevItemsPage;
                        const nextEl = nextButton ? nextButton.$el : null;
                        const prevEl = prevButton ? prevButton.$el : null;

                        if ((nextButton || prevButton)
                            && (nextEl !== event.relatedTarget && prevEl !== event.relatedTarget)) {
                            this.hideTypeahead();
                        }
                    }
                });
            },
            hideSearchResults() {
                if (this.onlyExistingTags) {
                    // clear value and hide results on esc if onlyExistingTags
                    this.input = '';
                }
                if (!this.searchResults.length) {
                    // second esc press, unfocus input,
                    // to able to show search results on click in the input
                    this.input = '';
                    if (this.$refs.taginput) {
                        this.$refs.taginput.blur();
                    }
                    this.inputBlur();
                }
                this.$nextTick(() => {
                    this.ignoreSearchResults();
                });
            },
            searchChange(e) {
                this.$emit('search-change', e.target.value);
            },
            handleEmitPrevItemsPageClick() {
                this.$emit('load-prev-items-page');
            },
            handleEmitNextItemsPageClick() {
                this.$emit('load-next-items-page');
            },
        },
    };
</script>
